import React, { useEffect, useState, Children } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Package, Mail, LogOut, ArrowRight, CheckCircle2, Clock, Truck, Download, AlertCircle, X, MapPin, Plus, Minus, Trash2, Edit2, Lock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { subscribeToOrders, type Order, type OrderLineItem, type Address } from '../../../utils/firebase';
import { useCart } from '../../../contexts/CartContext';
import { recordProductSales } from '../../../utils/firebase';
import { getBookDownloadUrl } from '../../../utils/downloads';
import { shopData } from './shopData';
import { bookData } from './bookData';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function getItemImage(item: OrderLineItem): string | undefined {
  const pid = item.productId;
  const name = item.name?.trim().toLowerCase();
  if (item.type === 'book') {
    const book = bookData.find(b => b.id === pid) ?? bookData.find(b => b.lookupKey && b.lookupKey === pid) ?? bookData.find(b => b.stripePriceId && b.stripePriceId === pid) ?? bookData.find(b => b.title.trim().toLowerCase() === name);
    return book?.coverImage;
  }
  const product = shopData.find(s => s.id === pid) ?? shopData.find(s => s.lookupKey && s.lookupKey === pid) ?? shopData.find(s => s.name.trim().toLowerCase() === name);
  return product?.image;
}
function DownloadButton({
  orderId
}: {
  orderId: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        url
      } = await getBookDownloadUrl(orderId);
      // Open the signed URL in a new tab so the user keeps their account page.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.message ?? 'Download failed. Try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="flex flex-col items-start gap-2" data-id="element-1355">
      <button type="button" onClick={handleClick} disabled={loading} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 rounded-md" data-id="element-1356">
        {loading ? <>
            <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1357" />
            Preparing…
          </> : <>
            <Download className="w-3.5 h-3.5" data-id="element-1358" />
            Download
          </>}
      </button>
      {error && <p className="text-[10px] text-burgundy leading-relaxed max-w-xs" data-id="element-1359">
          {error}
        </p>}
    </div>;
}
function FulfillmentBadge({
  order
}: {
  order: Order;
}) {
  const config = (() => {
    switch (order.fulfillmentStatus) {
      case 'digital_delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'shipped':
        return {
          icon: Truck,
          label: 'Shipped',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'pending':
      default:
        return {
          icon: Clock,
          label: 'Processing',
          color: 'text-burgundy',
          bg: 'bg-burgundy/10'
        };
    }
  })();
  const Icon = config.icon;
  return <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${config.bg} ${config.color} text-[10px] font-bold uppercase tracking-[0.2em] rounded-md`} data-id="element-1360">
      <Icon className="w-3 h-3" data-id="element-1361" />
      {config.label}
    </span>;
}
function EditInfoModal({
  currentName,
  currentEmail,
  onClose,
  onSaveName,
  onSaveEmail
}: {
  currentName: string;
  currentEmail: string;
  onClose: () => void;
  onSaveName: (name: string) => Promise<void>;
  onSaveEmail: (email: string, password: string) => Promise<void>;
}) {
  const [name, setName] = useState(currentName);
  const [email, setEmail] = useState(currentEmail);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const emailChanged = email.trim().toLowerCase() !== currentEmail.toLowerCase();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (name.trim() !== currentName) {
        await onSaveName(name);
      }
      if (emailChanged) {
        if (!password) {
          throw new Error('Current password is required to change email.');
        }
        await onSaveEmail(email, password);
      }
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-1362">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-sm" data-id="element-1363" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" data-id="element-1364">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]" data-id="element-1365">
          <h2 className="text-xl font-bold text-[#111111]" data-id="element-1366">Edit Information</h2>
          <button onClick={onClose} className="text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1367">
            <X className="w-5 h-5" data-id="element-1368" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-1369">
          {success ? <div className="flex flex-col items-center justify-center py-8 text-center" data-id="element-1370">
              <div className="w-12 h-12 bg-tealslate/10 rounded-full flex items-center justify-center mb-4" data-id="element-1371">
                <CheckCircle2 className="w-6 h-6 text-tealslate" data-id="element-1372" />
              </div>
              <p className="text-lg font-bold text-[#111111] mb-2" data-id="element-1373">
                Information Updated
              </p>
              {emailChanged && <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1374">
                  A verification link has been sent to your new email address.
                  The change will take effect once you confirm it.
                </p>}
            </div> : <>
              <div data-id="element-1375">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1376">
                  Full Name
                </label>
                <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1377" />
              </div>

              <div data-id="element-1378">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1379">
                  Email Address
                </label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1380" />
              </div>

              {emailChanged && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="overflow-hidden" data-id="element-1381">
                  <div className="pt-2" data-id="element-1382">
                    <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1383">
                      Current Password *
                    </label>
                    <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1384" />
                    <p className="text-xs text-[#5A5A5A] mt-2 leading-relaxed" data-id="element-1385">
                      For security, your current password is required to change
                      your email address.
                    </p>
                  </div>
                </motion.div>}

              {error && <div className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-md" data-id="element-1386">
                  <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1387" />
                  <p className="text-xs text-burgundy leading-relaxed" data-id="element-1388">
                    {error}
                  </p>
                </div>}

              <div className="pt-4" data-id="element-1389">
                <button type="submit" disabled={loading || !name.trim() && !emailChanged} className="w-full px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1390">
                  {loading ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1391" />
                      Saving...
                    </> : 'Save Changes'}
                </button>
              </div>
            </>}
        </form>
      </motion.div>
    </div>;
}
function ChangePasswordModal({
  onClose,
  onChangePassword
}: {
  onClose: () => void;
  onChangePassword: (current: string, next: string) => Promise<void>;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onChangePassword(currentPassword, newPassword);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-1392">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-sm" data-id="element-1393" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" data-id="element-1394">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]" data-id="element-1395">
          <h2 className="text-xl font-bold text-[#111111]" data-id="element-1396">Change Password</h2>
          <button onClick={onClose} className="text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1397">
            <X className="w-5 h-5" data-id="element-1398" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-1399">
          {success ? <div className="flex flex-col items-center justify-center py-8 text-center" data-id="element-1400">
              <div className="w-12 h-12 bg-tealslate/10 rounded-full flex items-center justify-center mb-4" data-id="element-1401">
                <CheckCircle2 className="w-6 h-6 text-tealslate" data-id="element-1402" />
              </div>
              <p className="text-lg font-bold text-[#111111] mb-2" data-id="element-1403">
                Password Changed
              </p>
              <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1404">
                Your password has been successfully updated.
              </p>
            </div> : <>
              <div data-id="element-1405">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1406">
                  Current Password
                </label>
                <input type="password" required value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1407" />
              </div>

              <div data-id="element-1408">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1409">
                  New Password
                </label>
                <input type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1410" />
              </div>

              <div data-id="element-1411">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1412">
                  Confirm New Password
                </label>
                <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1413" />
              </div>

              {error && <div className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-md" data-id="element-1414">
                  <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1415" />
                  <p className="text-xs text-burgundy leading-relaxed" data-id="element-1416">
                    {error}
                  </p>
                </div>}

              <div className="pt-4" data-id="element-1417">
                <button type="submit" disabled={loading || !currentPassword || !newPassword || !confirmPassword} className="w-full px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1418">
                  {loading ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1419" />
                      Updating...
                    </> : 'Update Password'}
                </button>
              </div>
            </>}
        </form>
      </motion.div>
    </div>;
}
export function Account() {
  const {
    user,
    profile,
    loading: authLoading,
    signOut,
    resendVerification,
    updateDisplayName,
    updateEmail,
    changePassword
  } = useAuth();
  const navigate = useNavigate();
  const {
    items,
    clearCart
  } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderSuccess = searchParams.get('order') === 'success';
  const [showSuccess, setShowSuccess] = useState(orderSuccess);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);
  const [showEditInfo, setShowEditInfo] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const rawTab = searchParams.get('tab') as 'profile' | 'orders' | 'digital' | 'addresses' | 'cart' | null;
  // Addresses now live inside the Profile tab, so map old links onto Profile.
  const initialTab: 'profile' | 'orders' | 'digital' | 'cart' | null = rawTab === 'addresses' ? 'profile' : rawTab;
  const [activeTab, setActiveTab] = useState<'profile' | 'orders' | 'digital' | 'cart'>(initialTab || 'orders');
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const handleResendVerification = async () => {
    setResendState('sending');
    setResendError(null);
    try {
      await resendVerification();
      setResendState('sent');
      setTimeout(() => setResendState('idle'), 6000);
    } catch (err: any) {
      setResendState('error');
      setResendError(err?.message ?? 'Could not send verification email. Try again.');
    }
  };
  useEffect(() => {
    if (orderSuccess) {
      // Tally the just-purchased supplements so best-seller ranking reflects
      // real demand. (In production the Stripe webhook is the authoritative
      // writer; this is the client-side bridge so the system also accumulates
      // sales immediately after checkout.) Capture items BEFORE clearing.
      const supplementSales = items.filter(i => i.type === 'supplement').map(i => ({
        productId: i.productId,
        quantity: i.quantity
      }));
      if (supplementSales.length > 0) {
        recordProductSales(supplementSales).catch(err => console.error('[Sales] Failed to record product sales:', err));
      }
      clearCart();
      const next = new URLSearchParams(searchParams);
      next.delete('order');
      setSearchParams(next, {
        replace: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSuccess]);
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', {
        state: {
          from: '/account'
        },
        replace: true
      });
    }
  }, [user, authLoading, navigate]);
  // Subscribe to orders
  useEffect(() => {
    if (!user) return;
    setOrdersLoading(true);
    setOrdersError(null);
    const unsub = subscribeToOrders(user.uid, next => {
      setOrders(next);
      setOrdersLoading(false);
    });
    return unsub;
  }, [user]);
  const handleSignOut = async () => {
    await signOut();
    navigate('/', {
      replace: true
    });
  };
  if (authLoading || !user) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex items-center justify-center pt-32 pb-20" data-id="element-1420">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1421" />
      </div>;
  }
  const displayName = profile?.displayName ?? user.displayName ?? 'there';
  const firstName = displayName.split(' ')[0];
  const displayOrders = orders.map(o => ({
    ...o,
    lineItems: o.lineItems.filter(i => activeTab === 'digital' ? i.type === 'book' : i.type !== 'book')
  })).filter(o => o.lineItems.length > 0);
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-1422">
      <AnimatePresence data-id="element-1423">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed top-24 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl" data-id="element-1424">
            <div className="bg-tealslate text-white shadow-2xl rounded-xl p-5 flex items-start gap-4" data-id="element-1425">
              <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" data-id="element-1426" />
              <div className="flex-1" data-id="element-1427">
                <p className="font-bold text-lg mb-1" data-id="element-1428">Order received.</p>
                <p className="text-sm text-white/90 leading-relaxed" data-id="element-1429">
                  Thanks for your purchase — your order will appear below in a
                  moment. A receipt has been emailed to you.
                </p>
              </div>
              <button onClick={() => setShowSuccess(false)} className="text-white/70 hover:text-white transition-colors" aria-label="Dismiss" data-id="element-1430">
                <X className="w-4 h-4" data-id="element-1431" />
              </button>
            </div>
          </motion.div>}

        {showEditInfo && <EditInfoModal currentName={displayName} currentEmail={user.email ?? ''} onClose={() => setShowEditInfo(false)} onSaveName={updateDisplayName} onSaveEmail={updateEmail} data-id="element-1432" />}

        {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} onChangePassword={changePassword} data-id="element-1433" />}
      </AnimatePresence>

      {/* HEADER */}
      <section className="pt-32 pb-8 px-6" data-id="element-1434">
        <div className="max-w-5xl mx-auto" data-id="element-1435">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1436">
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#111111] mb-2 tracking-tight" data-id="element-1437">
              Welcome back, Jack!
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm font-medium text-[#5A5A5A]" data-id="element-1438">
              Manage your profile and orders
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="pb-24 px-6" data-id="element-1439">
        <div className="max-w-5xl mx-auto" data-id="element-1440">
          {/* MAIN CONTENT */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="w-full" data-id="element-1441">
            <div className="flex items-center gap-4 border-b border-[#E5E5E5] mb-8 overflow-x-auto hide-scrollbar" data-id="element-1442">
              <button onClick={() => {
              setActiveTab('profile');
              setSearchParams({
                tab: 'profile'
              });
            }} className={`pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'profile' ? 'border-tealslate text-[#111111]' : 'border-transparent text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1443">
                Profile
              </button>
              <button onClick={() => {
              setActiveTab('orders');
              setSearchParams({});
            }} className={`pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'orders' ? 'border-tealslate text-[#111111]' : 'border-transparent text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1444">
                Orders
              </button>
              <button onClick={() => {
              setActiveTab('digital');
              setSearchParams({
                tab: 'digital'
              });
            }} className={`pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'digital' ? 'border-tealslate text-[#111111]' : 'border-transparent text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1445">
                Digital Orders
              </button>
              <button onClick={() => {
              setActiveTab('cart');
              setSearchParams({
                tab: 'cart'
              });
            }} className={`pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'cart' ? 'border-tealslate text-[#111111]' : 'border-transparent text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1446">
                Cart
              </button>
            </div>

            {activeTab === 'profile' ? <div className="space-y-8" data-id="element-1447">
                <div className="max-w-2xl space-y-4" data-id="element-1448">
                  <div className="flex items-center justify-between gap-4" data-id="element-1449">
                    <h2 className="text-2xl font-bold text-[#111111] tracking-tight" data-id="element-1450">
                      Account Information
                    </h2>
                    <button onClick={() => setShowEditInfo(true)} className="shrink-0 text-xs font-bold py-2 px-3 bg-white border border-[#E5E5E5] rounded-md text-[#111111] hover:border-tealslate hover:text-tealslate transition-colors flex items-center gap-2 shadow-sm" aria-label="Edit account information" data-id="element-1451">
                      <Edit2 className="w-3.5 h-3.5" data-id="element-1452" />
                      Edit
                    </button>
                  </div>

                  <div className="bg-white border border-[#E5E5E5] p-6 md:p-8" data-id="element-1453">
                    <dl className="divide-y divide-[#E5E5E5]" data-id="element-1454">
                      <div className="flex items-center justify-between gap-4 py-3 first:pt-0" data-id="element-1455">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1456">
                          Name
                        </dt>
                        <dd className="text-sm font-medium text-[#111111] text-right" data-id="element-1457">
                          {displayName}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3" data-id="element-1458">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1459">
                          Email
                        </dt>
                        <dd className="text-sm font-medium text-[#111111] text-right break-all flex items-center gap-2" data-id="element-1460">
                          <Mail className="w-4 h-4 text-[#9A9A90] shrink-0" data-id="element-1461" />
                          {user.email}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3 last:pb-0" data-id="element-1462">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1463">
                          Password
                        </dt>
                        <dd className="flex items-center gap-3" data-id="element-1464">
                          <span className="text-sm font-medium text-[#111111] tracking-widest" data-id="element-1465">
                            ••••••••
                          </span>
                          <button onClick={() => setShowChangePassword(true)} className="text-xs font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1466">
                            Change
                          </button>
                        </dd>
                      </div>
                    </dl>

                    {!user.emailVerified && <div className="mt-6 pt-6 border-t border-[#E5E5E5]" data-id="element-1467">
                        <div className="flex items-start gap-2 mb-4" data-id="element-1468">
                          <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1469" />
                          <p className="text-xs text-[#5A5A5A] leading-relaxed" data-id="element-1470">
                            Email not verified. Check your inbox for a
                            verification link.
                          </p>
                        </div>
                        <button onClick={handleResendVerification} disabled={resendState === 'sending' || resendState === 'sent'} className="w-full text-xs font-bold py-3 px-4 border border-[#E5E5E5] rounded-md text-[#111111] hover:border-tealslate hover:text-tealslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2" data-id="element-1471">
                          {resendState === 'sending' && <>
                              <span className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" data-id="element-1472" />
                              Sending…
                            </>}
                          {resendState === 'sent' && <>
                              <CheckCircle2 className="w-4 h-4" data-id="element-1473" />
                              Verification email sent
                            </>}
                          {(resendState === 'idle' || resendState === 'error') && <>
                              <Mail className="w-4 h-4" data-id="element-1474" />
                              Resend verification email
                            </>}
                        </button>
                        {resendState === 'error' && resendError && <p className="text-xs text-burgundy mt-3 leading-relaxed" data-id="element-1475">
                            {resendError}
                          </p>}
                        {resendState === 'sent' && <p className="text-xs text-[#9A9A90] mt-3 leading-relaxed" data-id="element-1476">
                            Check your inbox (and spam folder). The link expires
                            in 24 hours.
                          </p>}
                      </div>}
                  </div>
                </div>

                <AddressBookTab data-id="element-1477" />

                <button onClick={handleSignOut} className="w-full max-w-2xl bg-white border border-[#E5E5E5] shadow-sm rounded-xl p-5 hover:border-tealslate transition-all group flex items-center justify-between" data-id="element-1478">
                  <span className="text-sm font-bold text-[#111111] group-hover:text-tealslate transition-colors" data-id="element-1479">
                    Sign Out
                  </span>
                  <LogOut className="w-4 h-4 text-[#9A9A90] group-hover:text-tealslate transition-colors" data-id="element-1480" />
                </button>
              </div> : activeTab === 'orders' || activeTab === 'digital' ? <>
                {ordersLoading ? <div className="bg-white border border-[#E5E5E5] p-12 flex items-center justify-center" data-id="element-1481">
                    <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1482" />
                  </div> : ordersError ? <div className="bg-white border border-[#E5E5E5] p-6 flex gap-3 items-start" data-id="element-1483">
                    <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1484" />
                    <p className="text-sm text-burgundy" data-id="element-1485">{ordersError}</p>
                  </div> : displayOrders.length === 0 ? <div className="bg-white border border-[#E5E5E5] p-12 text-center" data-id="element-1486">
                    <Package className="w-12 h-12 text-[#9A9A90] mx-auto mb-4" data-id="element-1487" />
                    <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1488">
                      {activeTab === 'digital' ? 'No digital orders yet.' : 'No physical orders yet.'}
                    </h3>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed mb-8 max-w-sm mx-auto" data-id="element-1489">
                      When you make a purchase, your orders will appear here.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1490">
                      <Link to="/books" className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1491">
                        Browse Books <ArrowRight className="w-4 h-4" data-id="element-1492" />
                      </Link>
                      <Link to="/shop" className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1493">
                        Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-1494" />
                      </Link>
                    </div>
                  </div> : <ul className="space-y-6" data-id="element-1495">
                    {displayOrders.map(order => <li key={order.id} className="bg-white border border-[#E5E5E5] overflow-hidden" data-id="element-1496">
                        {/* Header Strip */}
                        <div className="bg-[#F9F9F9] border-b border-[#E5E5E5] px-6 py-4 flex flex-wrap items-start justify-between gap-6" data-id="element-1497">
                          <div className="flex flex-wrap items-start gap-8 md:gap-12" data-id="element-1498">
                            <div data-id="element-1499">
                              <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-wider mb-1" data-id="element-1500">
                                Order Placed
                              </p>
                              <p className="text-sm text-[#111111]" data-id="element-1501">
                                {formatDate(order.createdAt)}
                              </p>
                            </div>
                            <div data-id="element-1502">
                              <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-wider mb-1" data-id="element-1503">
                                Total
                              </p>
                              <p className="text-sm text-[#111111]" data-id="element-1504">
                                {formatPrice(order.total)}
                              </p>
                            </div>
                            {order.shippingAddress && <div data-id="element-1505">
                                <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-wider mb-1" data-id="element-1506">
                                  Ship To
                                </p>
                                <p className="text-sm text-[#111111]" data-id="element-1507">
                                  {order.shippingAddress.line1}
                                </p>
                              </div>}
                          </div>
                          <div className="text-right" data-id="element-1508">
                            <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-wider mb-1" data-id="element-1509">
                              Order # {order.id.slice(0, 8).toUpperCase()}
                            </p>
                            <button className="text-sm text-tealslate hover:text-navyslate font-medium transition-colors" data-id="element-1510">
                              Order details
                            </button>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="p-6" data-id="element-1511">
                          <div className="mb-6" data-id="element-1512">
                            <h3 className="text-lg font-bold text-tealslate mb-1" data-id="element-1513">
                              {order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'digital_delivered' ? 'Delivered' : order.fulfillmentStatus === 'shipped' ? 'Shipped' : 'Processing'}
                            </h3>
                            {order.trackingNumber && <p className="text-sm text-[#5A5A5A] flex items-center gap-1.5" data-id="element-1514">
                                <Truck className="w-4 h-4" data-id="element-1515" />
                                {order.trackingCarrier ?? 'Tracking'} ·{' '}
                                {order.trackingNumber}
                              </p>}
                          </div>

                          <ul className="space-y-8" data-id="element-1516">
                            {order.lineItems.map((item, idx) => {
                      const image = getItemImage(item);
                      return <li key={idx} className="flex flex-col sm:flex-row items-start justify-between gap-6" data-id="element-1517">
                                  {/* Left side: details and actions */}
                                  <div className="flex-1" data-id="element-1518">
                                    <p className="font-bold text-[#111111] text-base mb-1" data-id="element-1519">
                                      {item.name}
                                    </p>
                                    <p className="text-sm text-[#5A5A5A] mb-4" data-id="element-1520">
                                      Qty {item.quantity} ·{' '}
                                      {item.type === 'book' ? 'Digital' : 'Physical'}{' '}
                                      ·{' '}
                                      {formatPrice(item.unitPrice * item.quantity)}
                                    </p>

                                    <div className="flex flex-wrap gap-3" data-id="element-1521">
                                      {item.type === 'book' && order.status === 'paid' && <DownloadButton orderId={order.id} data-id="element-1522" />}
                                      <Link to={item.type === 'book' ? '/books' : '/shop'} className="px-5 py-3 bg-[#F9F9F9] border border-[#E5E5E5] text-[#111111] text-xs font-bold uppercase tracking-wider hover:border-tealslate hover:text-tealslate transition-colors inline-flex items-center justify-center rounded-md" data-id="element-1523">
                                        Buy it again
                                      </Link>
                                    </div>
                                  </div>

                                  {/* Right side: image */}
                                  {image && <div className="w-60 h-60 sm:w-80 sm:h-80 shrink-0 overflow-hidden flex items-center justify-center p-2" data-id="element-1524">
                                      <img src={image} alt={item.name} className="w-full h-full object-contain" data-id="element-1525" />
                                    </div>}
                                </li>;
                    })}
                          </ul>
                        </div>
                      </li>)}
                  </ul>}
              </> : <CartTab data-id="element-1526" />}
          </motion.div>
        </div>
      </section>
    </div>;
}
function AddressBookTab() {
  const {
    profile,
    updateProfile
  } = useAuth();
  const addresses = profile?.addresses ?? [];
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const handleSetDefault = async (id: string) => {
    const updated = addresses.map(a => ({
      ...a,
      isDefault: a.id === id
    }));
    await updateProfile({
      addresses: updated
    });
  };
  const handleDelete = async (id: string) => {
    const updated = addresses.filter(a => a.id !== id);
    // If we deleted the default, make the first remaining one default
    if (addresses.find(a => a.id === id)?.isDefault && updated.length > 0) {
      updated[0].isDefault = true;
    }
    await updateProfile({
      addresses: updated
    });
  };
  if (isAdding || editingId) {
    const addressToEdit = editingId ? addresses.find(a => a.id === editingId) : null;
    return <AddressForm initialData={addressToEdit} onCancel={() => {
      setIsAdding(false);
      setEditingId(null);
    }} onSave={async addr => {
      let updated: Address[];
      if (editingId) {
        updated = addresses.map(a => a.id === editingId ? {
          ...addr,
          id: editingId,
          isDefault: a.isDefault
        } : a);
      } else {
        const isFirst = addresses.length === 0;
        updated = [...addresses, {
          ...addr,
          id: Math.random().toString(36).substring(2, 9),
          isDefault: isFirst
        }];
      }
      await updateProfile({
        addresses: updated
      });
      setIsAdding(false);
      setEditingId(null);
    }} data-id="element-1527" />;
  }
  return <div className="space-y-6" data-id="element-1528">
      <div className="flex items-center justify-between" data-id="element-1529">
        <h2 className="text-2xl font-bold text-[#111111] tracking-tight" data-id="element-1530">
          Saved Addresses
        </h2>
        <button onClick={() => setIsAdding(true)} className="px-4 py-2 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate transition-colors rounded-md shadow-sm flex items-center gap-2" data-id="element-1531">
          <Plus className="w-4 h-4" data-id="element-1532" />
          Add New
        </button>
      </div>

      {addresses.length === 0 ? <div className="bg-white border border-[#E5E5E5] p-12 text-center" data-id="element-1533">
          <MapPin className="w-12 h-12 text-[#9A9A90] mx-auto mb-4" data-id="element-1534" />
          <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1535">
            No saved addresses.
          </h3>
          <p className="text-sm text-[#5A5A5A] leading-relaxed mb-8 max-w-sm mx-auto" data-id="element-1536">
            Add a shipping address to speed up your checkout process.
          </p>
          <button onClick={() => setIsAdding(true)} className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1537">
            Add Address
          </button>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1538">
          {addresses.map(addr => <div key={addr.id} className="bg-white border border-[#E5E5E5] p-6 flex flex-col" data-id="element-1539">
              <div className="flex-1" data-id="element-1540">
                <div className="flex items-start justify-between mb-2" data-id="element-1541">
                  <p className="font-bold text-[#111111]" data-id="element-1542">
                    {addr.recipientName}
                  </p>
                  {addr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-1543">
                      Default
                    </span>}
                </div>
                <p className="text-sm text-[#5A5A5A] leading-relaxed mb-6" data-id="element-1544">
                  {addr.line1}
                  {addr.line2 && <br data-id="element-1545" />}
                  {addr.line2}
                  <br data-id="element-1546" />
                  {addr.city}, {addr.state} {addr.postalCode}
                  <br data-id="element-1547" />
                  {addr.country}
                </p>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-[#E5E5E5]" data-id="element-1548">
                <button onClick={() => setEditingId(addr.id)} className="text-sm font-bold text-[#5A5A5A] hover:text-tealslate transition-colors flex items-center gap-1.5" data-id="element-1549">
                  <Edit2 className="w-3.5 h-3.5" data-id="element-1550" /> Edit
                </button>
                <button onClick={() => handleDelete(addr.id)} className="text-sm font-bold text-[#5A5A5A] hover:text-burgundy transition-colors flex items-center gap-1.5" data-id="element-1551">
                  <Trash2 className="w-3.5 h-3.5" data-id="element-1552" /> Delete
                </button>
                {!addr.isDefault && <button onClick={() => handleSetDefault(addr.id)} className="ml-auto text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1553">
                    Set as Default
                  </button>}
              </div>
            </div>)}
        </div>}
    </div>;
}
function AddressForm({
  initialData,
  onCancel,
  onSave
}: {
  initialData: Address | null;
  onCancel: () => void;
  onSave: (addr: Omit<Address, 'id' | 'isDefault'>) => Promise<void>;
}) {
  const [recipientName, setRecipientName] = useState(initialData?.recipientName ?? '');
  const [line1, setLine1] = useState(initialData?.line1 ?? '');
  const [line2, setLine2] = useState(initialData?.line2 ?? '');
  const [city, setCity] = useState(initialData?.city ?? '');
  const [state, setState] = useState(initialData?.state ?? '');
  const [postalCode, setPostalCode] = useState(initialData?.postalCode ?? '');
  const [country, setCountry] = useState(initialData?.country ?? 'United States');
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await onSave({
      recipientName: recipientName.trim(),
      line1: line1.trim(),
      line2: line2.trim(),
      city: city.trim(),
      state: state.trim(),
      postalCode: postalCode.trim(),
      country: country.trim()
    });
    setSubmitting(false);
  };
  return <div className="bg-white border border-[#E5E5E5] p-6 md:p-8" data-id="element-1554">
      <h2 className="text-xl font-bold text-[#111111] mb-6 tracking-tight" data-id="element-1555">
        {initialData ? 'Edit Address' : 'Add New Address'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1556">
        <div data-id="element-1557">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1558">
            Recipient Full Name *
          </label>
          <input type="text" required value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1559" />
        </div>
        <div data-id="element-1560">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1561">
            Address Line 1 *
          </label>
          <input type="text" required value={line1} onChange={e => setLine1(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1562" />
        </div>
        <div data-id="element-1563">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1564">
            Address Line 2 (Optional)
          </label>
          <input type="text" value={line2} onChange={e => setLine2(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1565" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1566">
          <div data-id="element-1567">
            <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1568">
              City *
            </label>
            <input type="text" required value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1569" />
          </div>
          <div className="grid grid-cols-2 gap-5" data-id="element-1570">
            <div data-id="element-1571">
              <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1572">
                State / Province *
              </label>
              <input type="text" required value={state} onChange={e => setState(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1573" />
            </div>
            <div data-id="element-1574">
              <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1575">
                ZIP / Postal *
              </label>
              <input type="text" required value={postalCode} onChange={e => setPostalCode(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1576" />
            </div>
          </div>
        </div>
        <div data-id="element-1577">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1578">
            Country *
          </label>
          <input type="text" required value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1579" />
        </div>

        <div className="flex items-center gap-3 pt-4" data-id="element-1580">
          <button type="submit" disabled={submitting} className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60" data-id="element-1581">
            {submitting ? 'Saving...' : 'Save Address'}
          </button>
          <button type="button" onClick={onCancel} disabled={submitting} className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm disabled:opacity-60" data-id="element-1582">
            Cancel
          </button>
        </div>
      </form>
    </div>;
}
function CartTab() {
  const {
    items,
    subtotal,
    itemCount,
    removeItem,
    updateQuantity
  } = useCart();
  const navigate = useNavigate();
  if (items.length === 0) {
    return <div className="bg-white border border-[#E5E5E5] p-12 text-center" data-id="element-1583">
        <div className="w-16 h-16 bg-[#F9F9F9] border border-[#E5E5E5] rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-1584">
          <Package className="w-7 h-7 text-[#9A9A90]" data-id="element-1585" />
        </div>
        <h3 className="text-xl font-bold text-[#111111] tracking-tight mb-3" data-id="element-1586">
          Your cart is empty.
        </h3>
        <p className="text-sm font-medium text-[#5A5A5A] mb-8 max-w-sm mx-auto leading-relaxed" data-id="element-1587">
          Browse the bookshop or shop to add items.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1588">
          <Link to="/books" className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-1589">
            Browse Books <ArrowRight className="w-4 h-4" data-id="element-1590" />
          </Link>
          <Link to="/shop" className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-1591">
            Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-1592" />
          </Link>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1593">
      <div data-id="element-1594">
        <h2 className="text-3xl font-bold text-[#111111] tracking-tight" data-id="element-1595">
          Shopping Cart
        </h2>
      </div>

      <div className="bg-white border border-[#E5E5E5]" data-id="element-1596">
        {/* Column header */}
        <div className="px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-end" data-id="element-1597">
          <span className="text-sm font-medium text-[#9A9A90]" data-id="element-1598">Price</span>
        </div>

        <ul data-id="element-1599">
          {items.map(item => {
          const image = getItemImage(item as unknown as OrderLineItem);
          return <li key={item.productId} className="px-6 py-6 border-b border-[#E5E5E5] last:border-b-0 flex gap-5" data-id="element-1600">
                {/* Image */}
                {image && <div className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 flex items-center justify-center" data-id="element-1601">
                    <img src={image} alt="" className="max-w-full max-h-full object-contain" data-id="element-1602" />
                  </div>}

                {/* Details */}
                <div className="flex-1 min-w-0" data-id="element-1603">
                  <p className="text-base sm:text-lg font-bold text-[#111111] leading-snug" data-id="element-1604">
                    {item.name}
                  </p>
                  <p className="text-sm text-tealslate font-medium mt-1" data-id="element-1605">
                    In Stock
                  </p>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A90] mt-1" data-id="element-1606">
                    {item.type === 'book' ? 'Digital Book' : 'Supplement'}
                  </p>

                  {/* Controls row */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mt-4" data-id="element-1607">
                    <div className="flex items-center border border-[#E5E5E5] rounded-md overflow-hidden" data-id="element-1608">
                      <button onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="px-3 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Decrease quantity" data-id="element-1609">
                        <Minus className="w-4 h-4" data-id="element-1610" />
                      </button>
                      <span className="px-4 text-sm font-bold text-[#111111] min-w-[2rem] text-center" data-id="element-1611">
                        {item.quantity}
                      </span>
                      <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-3 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Increase quantity" data-id="element-1612">
                        <Plus className="w-4 h-4" data-id="element-1613" />
                      </button>
                    </div>

                    <span className="w-px h-5 bg-[#E5E5E5] hidden sm:block" data-id="element-1614" />

                    <button onClick={() => removeItem(item.productId)} className="text-sm font-medium text-[#5A5A5A] hover:text-burgundy transition-colors inline-flex items-center gap-1.5" data-id="element-1615">
                      <Trash2 className="w-4 h-4" data-id="element-1616" />
                      Delete
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="shrink-0 text-right" data-id="element-1617">
                  <p className="text-base sm:text-lg font-bold text-[#111111]" data-id="element-1618">
                    {formatPrice(item.unitPrice * item.quantity)}
                  </p>
                </div>
              </li>;
        })}
        </ul>

        {/* Subtotal line */}
        <div className="px-6 py-5 border-t border-[#E5E5E5] text-right" data-id="element-1619">
          <p className="text-lg text-[#111111]" data-id="element-1620">
            Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):{' '}
            <span className="font-bold" data-id="element-1621">{formatPrice(subtotal)}</span>
          </p>
        </div>
      </div>

      {/* Checkout block */}
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8" data-id="element-1622">
        <p className="text-sm text-[#5A5A5A] mb-6" data-id="element-1623">
          Shipping and taxes calculated at checkout.
        </p>
        <button onClick={() => navigate('/checkout')} className="w-full px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-3" data-id="element-1624">
          Pre-Order <ArrowRight className="w-4 h-4" data-id="element-1625" />
        </button>
      </div>
    </div>;
}